import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogActions, DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { MonitorButton, MonitorDialog } from "../../components/MonitorControls";
import DateLabel from "../../components/DateLabel";
import { v } from "../../utils/helpers";
import { stageLabel } from "../../utils/stages";
import StageErrorAlert from "./StageErrorAlert";

/**
 * Archiv der letzten Stufe: archivierte PROD-Karten mit Zeitpunkt und Person. Das Archiv liegt nur in der Webapp; ein
 * Freigebender kann eine Karte zurück auf das Board holen.
 */
export default function StageArchiveDialog({ board, canRestore, onRestore, onClose }) {
  const { t } = useTranslation(["freigabe", "gemeinsam"]);
  const titleId = useId();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const rows = board?.archive || [];
  const labelOf = key => board?.stages?.find(stage => stage.key === key)?.label || stageLabel(key);
  const restore = async row => {
    setBusy(row.candidateId); setError(null);
    try { await onRestore([row.candidateId]); }
    catch (failure) { setError({ code: failure?.code || null, message: failure?.message || "", details: failure?.details }); }
    finally { setBusy(null); }
  };
  return <MonitorDialog className="promotion-dialog sb-dialog" maxWidth="md" aria-labelledby={titleId} onClose={() => { if (!busy) onClose(); }}>
    <DialogTitle id={titleId}>{t("stageBranches.archive.title")}</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: v("text-2") }}>{t("stageBranches.archive.hint")}</Typography>
        {!rows.length ? <Typography variant="body2" sx={{ color: v("text-2") }}>{t("stageBranches.archive.empty")}</Typography>
          : <TableContainer>
            <Table size="small" aria-labelledby={titleId}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("stageBranches.archive.package")}</TableCell>
                  <TableCell>{t("stageBranches.archive.stage")}</TableCell>
                  <TableCell>{t("stageBranches.archive.archivedAt")}</TableCell>
                  <TableCell>{t("stageBranches.archive.archivedBy")}</TableCell>
                  {canRestore && <TableCell align="right">{t("stageBranches.archive.actions")}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => <TableRow key={row.candidateId}>
                  <TableCell sx={{ overflowWrap: "anywhere" }}>
                    <Typography variant="body2" sx={{ color: v("text-1"), fontWeight: 600 }}>{row.packageName || row.packageId}</Typography>
                    {row.packageName && row.packageName !== row.packageId && <Typography variant="body2" sx={{ color: v("text-2") }}>{row.packageId}</Typography>}
                  </TableCell>
                  <TableCell>{row.targetName ? `${labelOf(row.stage)} · ${row.targetName}` : labelOf(row.stage)}</TableCell>
                  <TableCell><DateLabel value={row.archivedAt}/></TableCell>
                  <TableCell sx={{ overflowWrap: "anywhere" }}>{row.archivedByName || row.archivedBy}</TableCell>
                  {canRestore && <TableCell align="right">
                    <MonitorButton variant="outlined" disabled={!!busy} onClick={() => restore(row)}
                      aria-label={t("stageBranches.archive.restoreOne", { name: row.packageName || row.packageId })}>
                      {busy === row.candidateId ? t("promotion.sending") : t("stageBranches.archive.restore")}
                    </MonitorButton>
                  </TableCell>}
                </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>}
        <StageErrorAlert error={error} title={t("promotion.actionError")}/>
      </Stack>
    </DialogContent>
    <DialogActions>
      <MonitorButton autoFocus disabled={!!busy} onClick={onClose}>{t("gemeinsam:aktion.schliessen")}</MonitorButton>
    </DialogActions>
  </MonitorDialog>;
}
